/*
  Algolia "Search & Discovery" — official Custom Hooks customization.
  https://support.algolia.com → "[Shopify] How to set up Custom Hooks"

  The no-code app has no autocomplete template editor, so we extend the product
  row through the supported `beforeAutocompleteProductTemplate` hook. It runs
  inside the app's own render using data already in the Algolia index — NO extra
  network requests, no DOM observers.

  This app version's hook signature (confirmed via console):
    (defaultTemplate, { item, html, components })
  Returning `html`…`` replaces the row; returning `defaultTemplate` keeps the app
  default (used as a safe fallback so a field/API mismatch can't break the row).
*/
function ndiMoney(n) {
  // Index prices are dollar amounts (e.g. 3945, 338.15). USD only, which is fine
  // for this single-currency store.
  // ponytail: hardcoded USD; switch to the app's markets/currency helper if NDI
  // ever sells in multiple currencies.
  return '$' + Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function ndiShortDesc(item) {
  // body_html_safe wraps the real summary in [shortdesc]…[/shortdesc] (followed by
  // a [properties] block). Pull out just the summary; fall back to the raw text.
  var raw = item.body_html_safe || '';
  var m = raw.match(/\[shortdesc\]([\s\S]*?)\[\/shortdesc\]/i);
  var desc = (m ? m[1] : raw).replace(/\[[^\]]*\]/g, ' ').replace(/\s+/g, ' ').trim();
  return desc.length > 120 ? desc.slice(0, 120).trim() + '…' : desc;
}

function ndiSkuKey(s) {
  // "FG-50G-NEW" / "fg 50g" -> "FG50G": drop separators and the condition suffix.
  return String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/(NEW|NOB|REF)$/, '');
}

// Fetch a wider pool than we show, so the base model is in the set we re-order.
var NDI_AA_FETCH = 20;
var ndiAaShow = 8; // overwritten with the app's own hitsPerPage when it sets one

function ndiSortHits(list, query) {
  // Algolia splits "FG-50G" on the hyphen, so FG-50G, FG-50G-DSL and FG-50G-5G all tie
  // on relevance. Re-order: SKUs that start with the query first, shortest SKU first
  // (exact match is the shortest); licenses last. Sort is stable, so Algolia's order
  // is kept within ties.
  var q = ndiSkuKey(query);
  if (!Array.isArray(list)) return list;
  if (!q) return list.slice(0, ndiAaShow);
  function rank(hit) {
    var key = ndiSkuKey(hit.sku);
    if (key.indexOf(q) === 0) return key.length; // typed a license SKU? it still ranks
    // ponytail: non-prefix hits keep Algolia order
    return hit.product_type === 'License' ? 1e6 : 1e5;
  }
  return list
    .slice()
    .sort(function (a, b) {
      return rank(a) - rank(b);
    })
    .slice(0, ndiAaShow);
}

function ndiRegisterAlgoliaHooks() {
  if (!window.algoliaShopify || !algoliaShopify.hooks || !algoliaShopify.hooks.registerHook) {
    return false;
  }

  // Widen the products query; ndiSortHits trims back to the display count.
  algoliaShopify.hooks.registerHook('beforeAutocompleteConfigurationOptions', function (config, context) {
    if (!config || !config.queries || !context || context.sourceId !== 'products') return config;
    config.queries = config.queries.map(function (query) {
      var params = Object.assign({}, query.params);
      if (params.hitsPerPage && params.hitsPerPage < NDI_AA_FETCH) ndiAaShow = params.hitsPerPage;
      params.hitsPerPage = Math.max(params.hitsPerPage || 0, NDI_AA_FETCH);
      return Object.assign({}, query, { params: params });
    });
    return config;
  });

  // Data hook — runs on the response before the dropdown renders (no DOM work).
  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteProductTransformResponseHits',
    function (hits, results) {
      try {
        var res = Array.isArray(results) ? results[0] : results;
        var query = res && res.query;
        // Autocomplete passes one hits array per query; tolerate a flat array too.
        if (Array.isArray(hits) && Array.isArray(hits[0])) {
          return hits.map(function (list) {
            return ndiSortHits(list, query);
          });
        }
        return ndiSortHits(hits, query);
      } catch (e) {
        return hits; // never break the dropdown
      }
    }
  );

  // Products-only autocomplete: rebuild the dropdown layout with just the
  // products section, dropping collections / pages / articles / suggestions.
  // The app renders each source into `elements.<sourceId>` and assembles them
  // in this main template — omit `elements.collections` and it's gone.
  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteMainTemplate',
    function (defaultTemplate, templateOptions, elements) {
      var html = templateOptions && templateOptions.html;
      if (!html || !elements) return defaultTemplate; // never break the dropdown
      return html`
        <div class="aa-PanelLayout aa-Panel--scrollable">${elements.products || ''}</div>
      `;
    }
  );

  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteProductTemplate',
    function (defaultTemplate, params) {
      var item = params && params.item;
      var html = params && params.html;
      var components = params && params.components;
      if (!item || !html) return defaultTemplate;

      try {
        var desc = ndiShortDesc(item);
        var title =
          components && components.Highlight
            ? components.Highlight({ hit: item, attribute: 'title' })
            : item.title;
        var onSale =
          item.compare_at_price && Number(item.compare_at_price) > Number(item.price);

        return html`
          <a href="${item.url || '/products/' + item.handle}" class="aa-ItemLink aa-ProductItem">
            <div class="aa-ItemContent">
              <div class="aa-ItemPicture aa-ItemPicture--loaded">
                <img src="${item.product_image}" alt="${item.title}" loading="lazy" />
              </div>
              <div class="aa-ItemContentBody">
                <div class="aa-ItemContentTitleWrapper">
                  <div class="aa-ItemContentTitle">${title}</div>
                </div>
                ${desc && html`<div class="aa-ItemContentDescription">${desc}</div>`}
                <div class="aa-ItemContentPrice">
                  <span class="aa-ItemContentPriceCurrent">${ndiMoney(item.price)}</span>
                  ${onSale
                    ? html`<span class="ndi-aa-was">${ndiMoney(item.compare_at_price)}</span>`
                    : ''}
                </div>
              </div>
            </div>
          </a>
        `;
      } catch (e) {
        return defaultTemplate; // never break the row if the API differs
      }
    }
  );
  return true;
}

// Register as soon as the API is available — immediately if it's already up,
// otherwise when the app fires its init event (timing-robust).
if (!ndiRegisterAlgoliaHooks()) {
  document.addEventListener('algolia.hooks.initialize', ndiRegisterAlgoliaHooks);
}
